<template>
    <admin-layout 
    title="View Notification"
    show-back
    backUrl="/contents/system-notification">
        <div>
            <TabComponent
                :tabs="tabs"
            >
            </TabComponent>
        </div>
        <div class="p-7" v-if="currentRoutePath !== `/contents/system-notification/edit/${route.params.id}/logs`">
            <FormSection>
                <template #title>
                    Notification Information
                </template>

                <template #description>
                    Relevant data and other details
                </template>

                <template #form>
                    <div class="col-span-12">
                        <TextInput 
                            label="Subject"
                            placeholder="Subject"
                            v-model="payload.subject"
                            id="subject"
                            name="subject"
                        />
                    </div>
                    <div class="col-span-12">
                        <TextInput 
                            textarea
                            label="Body"
                            v-model="payload.body"
                            id="body"
                        />
                    </div>
      
                    <div class="col-span-12 text-gray-600" v-if="item?.body_smart_tags">
                        <label class="block text-sm">
                            Required smart tags for body:
                        </label>
                        
                        <p>
                            {{ item?.body_smart_tags.join(', ') }}
                        </p>
                    </div>
                </template>
                <template #actions>
                    <ButtonComponent v-if="$hasPermission('update-content')" @click="showConfirmation = true">
                        Save Changes
                    </ButtonComponent>
                </template>
            </FormSection>
                    
        </div>

        <div v-else>
            <ActivityLogsTable
                :logs="logs"
                @fetch-index="editEmailNotification"
            />
        </div>

        <PageLoader v-if="loader" />

        <ErrorModal
            width="w-[548px]"
            title="Error"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
            :errors="errors"
        >
            <template #content>
                <div v-html="errorMsg"/>
            </template>
        </ErrorModal>

        <SuccessModal
            :show="showSuccess"
            @cancel="showSuccess = false"
            @confirm="showSuccess = false"
            action-text="Confirm"
            title="System Notification Updated!"
            content="System Notification has been updated successfully"
        />

        <ConfirmationModal
            :show="showConfirmation"
            @cancel="showConfirmation = false"
            @confirm="showConfirmation = false; updateSystemNotification() "
            cancel-text="Cancel"
            action-text="Confirm"
            title="Update System Notification?"
            content="Are you sure you wish to update this System Notification?"
        />
       
    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import TabComponent from '@/components/TabComponent.vue';
import FormSection from '@/components/FormSection.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import ActivityLogsTable from '@/components/ActivityLogsTable.vue';
import SmartTags from '@/components/SmartTags.vue';
import CKEditor from '@/components/inputs/CKEditor.vue';

import SystemNotification from "@/classes/SystemNotification";

import { useRoute, useRouter } from 'vue-router';
import { onMounted, ref, computed } from 'vue';

const route = useRoute();
const router = useRouter();

// Data
const loader    = ref(false);
const showError = ref(false);
const errorMsg  = ref(null);
const errors      = ref([])
const showSuccess = ref(false)
const showConfirmation = ref(false)

const item = ref(null);

const payload = computed(() => {
    return {
        id: route.params.id,
        subject: null,
        body: null
    }
})

const currentRoutePath = computed(() => router.currentRoute.value.path);

const log_counts      = ref(0);
const logs            = ref({});

// Methods
const editSystemNotification = () => {
    loader.value = true;

    SystemNotification.editSystemNotification(route.params.id)
        .then((response: any) => {
            loader.value = false;

            item.value = response.data.systemNotification;
            payload.value.subject = item.value.subject;
            payload.value.body = item.value.body;

            log_counts.value = response?.data.log_counts
            logs.value = response?.data.logs
        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

const updateSystemNotification = () => {
    loader.value = true;

    SystemNotification.updateSystemNotification(route.params.id, payload.value)
        .then((response: any) => {
            loader.value = false;
            showSuccess.value = true;
            item.value = response.data.systemNotification;
        })
        .catch((err) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = err.response.data.message;

            if(err.response.status == 422) {
                errors.value = err.response.data.errors;
            }
            
            if(err.response.status == 401) {
                window.location.replace('/login');
            }
        })
}

const tabs: { name: string, count: number, href?: string, allowed: boolean }[] = computed(() => [
    {
        name: 'Information',
        count: 0,
        href: `/contents/system-notification/edit/${route.params.id}`,
        allowed: true
    },
    {
        name: 'Activity logs',
        count: log_counts.value,
        href: `/contents/system-notification/edit/${route.params.id}/logs`,
        allowed: true
    },
]);

onMounted( () => {
    editSystemNotification();
});

</script>